import React, { useEffect, useRef, useState } from 'react';
import MapService from 'common/services/Map';
import useDebounce from 'common/customHooks/useDebounce';
import useOnClickOutside from 'common/customHooks/useOnClickOutside';
import { PinIcon } from './Icons';

/**
 * `.search-field` — bouton de géolocalisation, saisie libre, bouton de recherche.
 *
 * Deux variantes de la maquette, à rendu identique aux classes près : `modal`
 * (dans la popin, avec l'émoji 📍) et `inline` (dans l'en-tête du listing, avec
 * l'icône SVG et la classe `.locator-search`).
 *
 * La maquette ne câblait rien : le formulaire est ajouté ici pour que la touche
 * Entrée déclenche la recherche, ce qu'aucun `<div>` ne sait faire.
 *
 * Dropdown de suggestions : réutilise `common/customHooks/useDebounce`,
 * `common/customHooks/useOnClickOutside` et
 * `MapService.geoapifyService.getPlacePredictions` — même logique que
 * `common/components/Map/MapSearch` (2025_VOG), avec le balisage/CSS
 * `.search-field` propres à TCHIP.
 */
const SearchField = ({
  variant = 'inline',
  placeholder = 'Ville, code postal, adresse…',
  submitLabel = 'Rechercher',
  ariaLabel = 'Rechercher un salon',
  isBusy = false,
  onSearch,
  onSelectPrediction,
  onGeolocate,
  autoFocus = false,
}) => {
  const [query, setQuery] = useState('');
  const [predictions, setPredictions] = useState([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  // Dernière prédiction choisie, tant que la saisie n'a pas été retouchée.
  // Deux rôles : comme `alreadyGotPrediction` dans common/GeoapifySearch, elle
  // évite de rouvrir le dropdown juste après une sélection (quand `setQuery`
  // pose le texte formaté et redéclenche l'effet de debounce) ; et elle permet
  // à `submit` de rejouer la prédiction plutôt que de re-géocoder son libellé.
  const [selectedPrediction, setSelectedPrediction] = useState(null);
  const debouncedQuery = useDebounce(query, 300);
  const fieldRef = useRef(null);
  // React 17 ici, pas de useId : deux SearchField (inline + modal) coexistent,
  // il leur faut un id de listbox distinct pour aria-controls.
  const predictionsId = useRef(`search-field-predictions-${Math.random().toString(36).slice(2)}`).current;

  useOnClickOutside(fieldRef, () => setPredictions([]));

  useEffect(() => {
    const trimmed = debouncedQuery.trim();
    if (!trimmed || selectedPrediction) {
      setPredictions([]);
      return;
    }

    let cancelled = false;
    MapService.geoapifyService.getPlacePredictions(trimmed).then((results) => {
      if (cancelled) return;
      setPredictions(results || []);
      setActiveIndex(-1);
    });

    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, selectedPrediction]);

  const selectPrediction = (prediction) => {
    setQuery(prediction.formatted || '');
    setPredictions([]);
    setActiveIndex(-1);
    setSelectedPrediction(prediction);
    if (onSelectPrediction) onSelectPrediction(prediction);
  };

  const submit = (event) => {
    event.preventDefault();
    if (predictions.length) {
      selectPrediction(predictions[activeIndex >= 0 ? activeIndex : 0]);
      return;
    }
    // Prédiction déjà choisie et saisie inchangée : on rejoue ses coordonnées.
    // Repasser par la recherche texte re-géocoderait le libellé abrégé de
    // l'autocomplétion — et `/geocode/search` résout « Bordeaux, NAQ, France »
    // en « Bordeaux, place de la Concorde, 75008 Paris », d'où des résultats
    // parisiens après un simple clic sur « Rechercher ».
    if (selectedPrediction) {
      if (onSelectPrediction) onSelectPrediction(selectedPrediction);
      return;
    }
    if (onSearch) onSearch(query);
  };

  const onKeyDown = (event) => {
    if (!predictions.length) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index) => (index + 1) % predictions.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => (index <= 0 ? predictions.length - 1 : index - 1));
    } else if (event.key === 'Escape') {
      setPredictions([]);
      setActiveIndex(-1);
    }
  };

  return (
    <div
      className={['search-field-wrap', variant === 'inline' ? 'locator-search' : null].filter(Boolean).join(' ')}
      ref={fieldRef}
    >
      <form className="search-field" onSubmit={submit} role="search">
        <button
          type="button"
          className="search-field-geo"
          onClick={onGeolocate}
          aria-label="Me géolocaliser"
          title="Me géolocaliser"
        >
          {variant === 'modal' ? <span aria-hidden="true">📍</span> : <PinIcon size={16} />}
        </button>
        <input
          type="text"
          placeholder={placeholder}
          aria-label={ariaLabel}
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setActiveIndex(-1);
            setSelectedPrediction(null);
          }}
          onKeyDown={onKeyDown}
          autoComplete="off"
          role="combobox"
          aria-controls={predictionsId}
          aria-expanded={predictions.length > 0}
          aria-autocomplete="list"
          autoFocus={autoFocus}
        />
        <button type="submit" className="search-field-btn" disabled={isBusy}>
          {isBusy ? (
            <span className="search-field-btn-loading">
              Recherche
              <span className="loading-dots" aria-hidden="true">
                <span className="loading-dots-dot" />
                <span className="loading-dots-dot" />
                <span className="loading-dots-dot" />
              </span>
            </span>
          ) : (
            submitLabel
          )}
        </button>
      </form>

      {predictions.length > 0 && (
        <ul id={predictionsId} className="search-field-predictions" role="listbox">
          {predictions.map((prediction, index) => (
            <li key={prediction.place_id || prediction.formatted} role="option" aria-selected={index === activeIndex}>
              <button
                type="button"
                className={index === activeIndex ? 'is-active' : undefined}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => selectPrediction(prediction)}
              >
                {prediction.formatted}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SearchField;
